import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from './URLCatalog';


interface PresenteeismSummary {
    orgName: string
    total: number
    onTime: number
    late: number
    withoutShift: number
    records: PresenteeismUserRecordSummary[]
    averageCheckInHour: string
    averageCheckOutHour: string
    totalHoursWorked: string 
    totalExtraHoursWorked: string
}

export interface PresenteeismUserRecordSummary {
    userName: string
    buildingName: string
    userID?: string
    requesterWhatsappID?: string
    dni?: string
    empresa?: string
    /**
     * Role the collaborator picked at the kiosk for this fichada. Unlike
     * `empresa`, which comes from the profile, this varies day to day.
     */
    selectedRole?: { id: string; label: string }
    /**
     * Role from the collaborator's profile, used when the fichada carries no kiosk
     * selection. Same for every record of that person.
     */
    collaboratorRole?: { id: string; label: string }
    date: string
    checkInHour: string
    checkOutHour: string
    totalHoursWorked: string // HH:MM:ss format
    extraHoursWorked: string // HH:MM:ss format
    minutesWorkedRaw?: number
    displayWorkedHours?: number
    timestamp: number 
}

// QontactPoint creation interface
export interface CreateQontactPointRequest {
    name: string;
    latitude: number;
    longitude: number;
    /** Check-in range in meters; omitted = the organization default applies */
    triggerDistance?: number;
}

// QontactPoint update interface
export interface UpdateQontactPointRequest {
    name: string;
    latitude: number;
    longitude: number;
}

export class QontactPointsService {
	private httpClient: HTTPClientInterface;

	constructor(httpClient: HTTPClientInterface) {
		this.httpClient = httpClient;
	}

	async getPresenteeismResumen(orgId: string, buId: string, yearMonth: string): Promise<PresenteeismSummary> {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.getPresenteeismResumen, {
			org_id: orgId,
			bu_id: buId,
		});
        request.method = "POST"
        request.body = {
            yearMonth: yearMonth
        }
        const response = await this.httpClient.execute(request);
        return response.result;
	}

	async getPaginated(orgId: string, pageSize: string, lastKey: string, opts?: { signal?: AbortSignal }) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.getPaginated, {
			org_id: orgId,
			pageSize,
			lastKey,
		});
		request.method = 'GET';
		if (opts?.signal) request.signal = opts.signal;
        const response = await this.httpClient.execute(request);
        return response.result;
	}

	async getById(qontactPointId: string) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.getById, {
			qontactPoint_id: qontactPointId,
		});
		request.method = 'GET';
        const response = await this.httpClient.execute(request);
        return response.result;
	}

	async createQontactPoint(orgId: string, qontactPointData: CreateQontactPointRequest) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.postCreate, { org_id: orgId });
		request.method = 'POST';
		request.body = qontactPointData;
		return this.httpClient.execute(request);
	}

	async updateQontactPoint(buildingId: string, updateData: UpdateQontactPointRequest) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.putUpdate, { building_id: buildingId });
		request.method = 'PUT';
		request.body = updateData;
		return this.httpClient.execute(request);
	}

	/** Check-in range of the qontact point, in meters */
	async updateTriggerDistance(orgId: string, qontactPointId: string, triggerDistance: number) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.putTriggerDistance, {
			org_id: orgId,
			qp_id: qontactPointId,
		});
		request.method = 'PUT';
		request.body = { triggerDistance };
		return this.httpClient.execute(request);
	}

	/** Organization default check-in range in meters (what a new qontact point uses without its own) */
	async getDefaultTriggerDistance(orgId: string): Promise<number | undefined> {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.getDefaultTriggerDistance, { org_id: orgId });
		request.method = 'GET';
		const response = await this.httpClient.execute(request);
		const value = Number(response.result?.triggerDistance);
		return Number.isFinite(value) && value > 0 ? value : undefined;
	}

	async deleteQontactPoint(qontactPointId: string) {
		const request = new HTTPRequest();
		request.url = buildURL(SystemURL.QontactPoints.deleteQontactPoint, {
			qontactPoint_id: qontactPointId,
		});
		request.method = 'DELETE';

		return this.httpClient.execute(request);
	}
}
